import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const ReportInterventi = () => {
  const navigate = useNavigate();
  const [myReports, setMyReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState('');

  useEffect(() => {
    const loadMyReports = async () => {
      try {
        setLoadingReports(true);
        setReportsError('');

        const res = await authFetch('/api/work/my-compiled-reports');
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(
            data?.error || 'Errore nel recupero dei rapportini compilati.'
          );
        }

        setMyReports(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Errore caricamento rapportini compilati:', err);
        setReportsError(err.message || 'Errore nel caricamento dei rapportini.');
      } finally {
        setLoadingReports(false);
      }
    };

    loadMyReports();
  }, []);

  const openCompiledReport = (item) => {
    if (!item) return;

    const isMaintenance =
      item.reportType === 'manutenzione' ||
      item.workOrder?.type === 'manutenzione';

    navigate(
      isMaintenance
        ? '/lavorazioni-in-corso/manutenzioni'
        : '/lavorazioni-in-corso/vit',
      {
        state: {
          fromWorkInProgress: item,
        },
      }
    );
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('it-IT');
  };
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        {/* Header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Report interventi
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Scegli se compilare un report partendo da una lavorazione assegnata
              oppure creare un report manuale.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Torna alla dashboard
          </button>
        </Card>

        {/* 2 box */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Box 1: Report assegnati */}
          <Card className="shadow-md p-5 flex flex-col">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Report assegnati
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              In futuro qui vedrai automaticamente i report generati dalle
              lavorazioni assegnate (interventi e manutenzioni) e potrai
              completarli o integrarli.
            </p>
            <div className="mt-auto">
              <button
                type="button"
                onClick={() => navigate('/report-assegnati')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition"
              >
                Compila report assegnato
              </button>
            </div>
          </Card>

          {/* Box 2: Compilazione manuale */}
          <Card className="shadow-md p-5 flex flex-col">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Compilazione manuale report
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Compila un nuovo report di manutenzione o una verifica VIT inserendo
              manualmente tutte le informazioni.
            </p>
            <div className="flex flex-wrap gap-3 mt-auto">
              <button
                type="button"
                onClick={() => navigate('/manutenzione')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
              >
                Manutenzione
              </button>
              <button
                type="button"
                onClick={() => navigate('/manutenzioni-multiple')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
              >
                Manutenzioni multiple
              </button>
              <button
                type="button"
                onClick={() => navigate('/vit')}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition"
              >
                VIT
              </button>
            </div>
          </Card>
          {/* Box 3: Rapportini compilati */}
          <Card className="shadow-md p-5 flex flex-col">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              I miei rapportini compilati
            </h2>

            <p className="text-sm text-gray-500 mb-4">
              Recupera i rapportini che hai già compilato, anche quelli creati manualmente
              a fine lavorazione.
            </p>

            {loadingReports && (
              <div className="text-sm text-gray-500">
                Caricamento rapportini...
              </div>
            )}

            {reportsError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {reportsError}
              </div>
            )}

            {!loadingReports && !reportsError && myReports.length === 0 && (
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                Non hai ancora rapportini compilati.
              </div>
            )}

            {!loadingReports && myReports.length > 0 && (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {myReports.map((item) => {
                  const order = item.workOrder || {};
                  const summary = item.summary || {};

                  const code =
                    order.internalCode ||
                    summary.codiceInterno ||
                    '-';

                  const title =
                    item.reportType === 'manutenzione'
                      ? 'Manutenzione'
                      : 'VIT';

                  const cliente =
                    summary.cliente ||
                    order.companyName ||
                    '-';

                  const luogo =
                    summary.luogo ||
                    order.place ||
                    '-';

                  const statusMap = {
                    aperta: 'Aperta',
                    in_corso: 'In corso',
                    sospesa: 'Sospesa',
                    chiusa: 'Chiusa',
                    completata: 'Completata',
                    annullata: 'Annullata',
                  };

                  const status = statusMap[order.status] || order.status || '-';

                  return (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => openCompiledReport(item)}
                      className="w-full text-left rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 px-3 py-3 transition"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-800">
                          {code}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {title}
                        </span>
                      </div>

                      <div className="text-xs text-gray-600">
                        <strong>Cliente:</strong> {cliente}
                      </div>

                      <div className="text-xs text-gray-600">
                        <strong>Luogo:</strong> {luogo}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        Data: {formatDate(item.generatedAt)} · Stato: {status}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        <div className="md:hidden flex justify-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Torna alla dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportInterventi;
